import {
  collection,
  getDocs,
  doc,
  setDoc,
  getDoc,
  query,
  limit
} from 'firebase/firestore';
import { db } from './firebase';
import {
  INITIAL_PRODUCTS,
  INITIAL_CATEGORIES,
  INITIAL_SCHOOLS,
  INITIAL_NEWS,
  INITIAL_SETTINGS,
  INITIAL_STATS
} from '../data/imminghamData';
import { normalizeProductSizes } from './inventoryService';

const SEED_CHECK_STORAGE_KEY = 'ul_firestore_seeded_v1';
let seedCheckInProgress: Promise<void> | null = null;
let seedCheckCompleted = false;

export async function seedFirestoreIfEmpty(): Promise<void> {
  // 1. Fast in-memory and persistent localStorage check
  if (seedCheckCompleted) {
    return;
  }

  try {
    if (typeof window !== 'undefined' && localStorage.getItem(SEED_CHECK_STORAGE_KEY) === 'true') {
      seedCheckCompleted = true;
      return;
    }
  } catch {
    // Non-blocking if storage restricted
  }

  // Prevent multiple concurrent seed checks
  if (seedCheckInProgress) {
    return seedCheckInProgress;
  }

  seedCheckInProgress = (async () => {
    try {
      // Check whether database is already populated with a single lightweight limit(1) query
      const checkSnap = await getDocs(query(collection(db, 'products'), limit(1)));
      if (!checkSnap.empty) {
        seedCheckCompleted = true;
        try {
          if (typeof window !== 'undefined') {
            localStorage.setItem(SEED_CHECK_STORAGE_KEY, 'true');
          }
        } catch {}
        return;
      }

      console.log('[Seed Service] Database appears uninitialized. Seeding initial catalogue datasets...');

      // 1. Seed Products
      for (const prod of INITIAL_PRODUCTS) {
        const { quantity, ...prodData } = prod as any;
        const cleanProduct = {
          ...prodData,
          sizes: normalizeProductSizes(prod.sizes, prod.size)
        };
        await setDoc(doc(db, 'products', prod.id), cleanProduct);
      }

      // 2. Seed Categories
      for (const cat of INITIAL_CATEGORIES) {
        await setDoc(doc(db, 'categories', cat.id), cat);
      }

      // 3. Seed Schools
      for (const sch of INITIAL_SCHOOLS) {
        await setDoc(doc(db, 'schools', sch.id), sch);
      }

      // 4. Seed News
      for (const newsItem of INITIAL_NEWS) {
        await setDoc(doc(db, 'news', newsItem.id), newsItem);
      }

      // 5. Seed Settings
      await setDoc(doc(db, 'settings', 'general'), INITIAL_SETTINGS);

      // 6. Seed Stats
      await setDoc(doc(db, 'stats', 'impact'), INITIAL_STATS);

      seedCheckCompleted = true;
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem(SEED_CHECK_STORAGE_KEY, 'true');
        }
      } catch {}
      console.log('[Seed Service] Seeding completed successfully.');
    } catch (err) {
      console.error('[Seed Service] Seeding check/execution error:', err);
    } finally {
      seedCheckInProgress = null;
    }
  })();

  return seedCheckInProgress;
}
